import { useEffect, useState, type MutableRefObject, type RefObject } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Card, Form, Table } from 'react-bootstrap'
import { useToast } from '../../components/ToastProvider'
import {
  fetchProductMonitoredAttributes,
  updateProductMonitoredAttributes,
} from '../../services/api'

export interface ProductComplianceScopeEditorProps {
  productId: string
  canEdit: boolean
  sectionRef?: RefObject<HTMLDivElement | null>
  title?: string
}

export default function ProductComplianceScopeEditor({
  productId,
  canEdit,
  sectionRef,
  title = 'Audit scope',
}: ProductComplianceScopeEditorProps) {
  const { showToast } = useToast()
  const queryClient = useQueryClient()
  const [scopeUseCustom, setScopeUseCustom] = useState(false)
  const [monitoredDraft, setMonitoredDraft] = useState<Record<string, boolean>>({})

  const { data: monitoredAttrsResponse, isLoading } = useQuery({
    queryKey: ['product-monitored-attributes', productId],
    queryFn: () => fetchProductMonitoredAttributes(productId),
    enabled: !!productId,
  })

  useEffect(() => {
    if (!monitoredAttrsResponse) return
    setScopeUseCustom(monitoredAttrsResponse.useCustomMonitoredAttributes)
    const d: Record<string, boolean> = {}
    for (const r of monitoredAttrsResponse.rows) {
      d[r.canonicalAttributeId] = r.monitored
    }
    setMonitoredDraft(d)
  }, [monitoredAttrsResponse])

  const saveMutation = useMutation({
    mutationFn: async () => {
      const ids = Object.entries(monitoredDraft)
        .filter(([, on]) => on)
        .map(([id]) => id)
      return updateProductMonitoredAttributes(productId, {
        useCustomMonitoredAttributes: scopeUseCustom,
        canonicalAttributeIds: scopeUseCustom ? ids : undefined,
      })
    },
    onSuccess: async () => {
      showToast('Audit scope saved.', { variant: 'success' })
      await queryClient.invalidateQueries({ queryKey: ['product-monitored-attributes', productId] })
      await queryClient.invalidateQueries({ queryKey: ['product-comparison-table', productId] })
    },
    onError: () => showToast('Failed to save audit scope.', { variant: 'danger' }),
  })

  return (
    <div
      ref={(node) => {
        if (!sectionRef) return
        ;(sectionRef as MutableRefObject<HTMLDivElement | null>).current = node
      }}
      id="audit-scope"
      tabIndex={-1}
    >
      <Card className="mb-3 border-primary-subtle">
        <Card.Body>
          <Card.Title className="h6">{title}</Card.Title>
          <p className="small text-muted mb-2">
            Enable a custom subset so distributor extraction and the comparison table focus on attributes you care about.
            Set API <code className="small">ComplianceSpike:ScopedExtraction</code> to true for this to apply server-side.
          </p>
          {isLoading && (
            <div className="small text-muted mb-2">Loading attributes…</div>
          )}
          <Form.Check
            type="switch"
            id={`scope-use-custom-${productId}`}
            className="mb-2"
            label="Limit audits to selected attributes"
            checked={scopeUseCustom}
            onChange={(e) => setScopeUseCustom(e.target.checked)}
            disabled={!canEdit}
          />
          {scopeUseCustom && (
            <div className="border rounded p-2 mb-2" style={{ maxHeight: 260, overflowY: 'auto' }}>
              <Table size="sm" className="mb-0" responsive>
                <thead>
                  <tr>
                    <th style={{ width: 40 }} />
                    <th>Attribute</th>
                    <th className="text-muted">Category</th>
                  </tr>
                </thead>
                <tbody>
                  {(monitoredAttrsResponse?.rows ?? []).map((r) => (
                    <tr key={r.canonicalAttributeId}>
                      <td>
                        <Form.Check
                          checked={monitoredDraft[r.canonicalAttributeId] ?? false}
                          onChange={(e) =>
                            setMonitoredDraft((prev) => ({
                              ...prev,
                              [r.canonicalAttributeId]: e.target.checked,
                            }))
                          }
                          aria-label={`Monitor ${r.displayLabel}`}
                          disabled={!canEdit}
                        />
                      </td>
                      <td>{r.displayLabel}</td>
                      <td className="text-muted small">{r.category}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
          <Button
            variant="primary"
            size="sm"
            disabled={saveMutation.isPending || !canEdit}
            onClick={() => saveMutation.mutate()}
          >
            Save audit scope
          </Button>
        </Card.Body>
      </Card>
    </div>
  )
}
